/**
 * Contract-specific hooks using TanStack Query
 * Provides data fetching, caching, and mutations for contract operations
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { PaginationState } from '@tanstack/react-table';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { useModuleGate } from '@/hooks/useAccess';
import { CONTRACT_READ_MODULES } from '@/lib/modules';
import { ContractsService, OpenAPI } from '@/api';
import type { billing_ActiveContractsSummaryResponse } from '@/api';
import type { billing_CreateContractRequest } from '@/api/models/billing_CreateContractRequest';
import type { billing_UpdateContractRequest } from '@/api/models/billing_UpdateContractRequest';
import type { billing_PayerRequest } from '@/api/models/billing_PayerRequest';
import type {
  Contract,
  CreateContractRequest,
  UpdateContractRequest,
  PayerRequest,
  TransformedContract,
  TransformedContractListResponse,
} from '@/types/billing';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import { format } from 'date-fns';
import { ContractTransformer } from '@/lib/fieldTransformers';
import { capacityKeys } from './useCapacity';
import { invalidateMoneyViews } from '@/hooks/reconciliation/invalidateMoneyViews';
import { usePaginatedQuery, type PaginatedResponse } from './usePagination';
import { errorToastText, extractBackendError, translateBackendError } from '@/lib/translateBackendError';
import { isInvoiceCollisionError } from '@/utils/contractErrorMessages';
import { apiErrorFromResponse } from '@/hooks/billing/fetchApiError';
import { retryUnlessClientError } from '@/lib/queryRetry';

// Query keys for contracts
export const contractKeys = {
  all: ['contracts'] as const,
  lists: () => [...contractKeys.all, 'list'] as const,
  // Callers pass optional scalars (status/childId) straight through, so the
  // filter record has to admit `undefined` — same shape as invoiceKeys.list.
  list: (filters: Record<string, string | number | boolean | undefined>) => [...contractKeys.lists(), { filters }] as const,
  listPaginated: (params: { limit: number; offset: number }) => [...contractKeys.lists(), 'paginated', params] as const,
  details: () => [...contractKeys.all, 'detail'] as const,
  detail: (id: string) => [...contractKeys.details(), id] as const,
  payers: (contractId: string) => [...contractKeys.detail(contractId), 'payers'] as const,
  // Root of the subsidy-amend preview namespace (see `subsidyAmendKeys` in
  // useContractSubsidyAmend.ts, which builds on this). It lives here so
  // useUpdateContract can invalidate the pending-amend banner without importing
  // the amend hook — that import would be a cycle, since the amend hook already
  // imports contractKeys.
  amends: () => [...contractKeys.all, 'subsidy-amend'] as const,
};

/**
 * Response type for paginated contracts list.
 * Matches the backend ContractListResponse struct.
 */
interface ContractListResponse {
  data: Contract[];
  total_count: number;
  limit: number;
  offset: number;
}

/**
 * Options for fetching paginated contracts
 */
export interface ContractsPaginatedOptions {
  /** Filter by status (comma-separated for multiple, e.g., "ACTIVE,UPCOMING") */
  status?: string;
  /** Filter by label IDs (comma-separated) */
  labelIds?: string;
  /** Filter by child ID */
  childId?: string;
  /** Search term to filter by contract number, child name, or notes */
  search?: string;
  /** Sort by column (default: "created_at") */
  sortBy?: string;
  /** Sort order (default: "desc") */
  sortOrder?: 'asc' | 'desc';
  /** Filter by subsidised flag (true/false; undefined => no filter) */
  subsidised?: boolean;
  /** Filter: any version of the group waits for a subsidy decision (true/false; undefined => no filter) */
  subsidyDecisionPending?: boolean;
  /** Filter: any version of the group has a staff-written note (true/false; undefined => no filter) */
  hasStaffNotes?: boolean;
  /** Filter: start_date on or after this date (YYYY-MM-DD) */
  startDateFrom?: string;
  /** Filter: start_date on or before this date (YYYY-MM-DD) */
  startDateTo?: string;
  /** Filter: end_date on or after this date (YYYY-MM-DD) */
  endDateFrom?: string;
  /** Filter: end_date on or before this date (YYYY-MM-DD) */
  endDateTo?: string;
  /** Filter: created_at on or after this date (YYYY-MM-DD) */
  createdFrom?: string;
  /** Filter: created_at on or before this date (YYYY-MM-DD) */
  createdTo?: string;
}

/**
 * Fetch paginated contracts from the API with filtering and sorting.
 * Uses direct fetch since the generated service doesn't include search/sort params yet.
 */
async function fetchContractsPaginated(
  limit: number,
  offset: number,
  options?: ContractsPaginatedOptions
): Promise<PaginatedResponse<TransformedContract>> {
  const baseUrl = OpenAPI.BASE || '';

  // Build query parameters
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });

  // Add optional filters
  if (options?.status) {
    params.set('status', options.status);
  }
  if (options?.labelIds) {
    params.set('label_ids', options.labelIds);
  }
  if (options?.childId) {
    params.set('child_id', options.childId);
  }
  if (options?.search) {
    params.set('search', options.search);
  }
  if (options?.sortBy) {
    params.set('sort_by', options.sortBy);
  }
  if (options?.sortOrder) {
    params.set('sort_order', options.sortOrder);
  }
  if (options?.subsidised !== undefined) {
    params.set('subsidised', String(options.subsidised));
  }
  if (options?.subsidyDecisionPending !== undefined) {
    params.set('subsidy_decision_pending', String(options.subsidyDecisionPending));
  }
  if (options?.hasStaffNotes !== undefined) {
    params.set('has_staff_notes', String(options.hasStaffNotes));
  }
  if (options?.startDateFrom) {
    params.set('start_date_from', options.startDateFrom);
  }
  if (options?.startDateTo) {
    params.set('start_date_to', options.startDateTo);
  }
  if (options?.endDateFrom) {
    params.set('end_date_from', options.endDateFrom);
  }
  if (options?.endDateTo) {
    params.set('end_date_to', options.endDateTo);
  }
  if (options?.createdFrom) {
    params.set('created_from', options.createdFrom);
  }
  if (options?.createdTo) {
    params.set('created_to', options.createdTo);
  }

  const url = `${baseUrl}/api/v1/contracts?${params.toString()}`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Add auth token if available
  if (typeof OpenAPI.TOKEN === 'string') {
    headers['Authorization'] = `Bearer ${OpenAPI.TOKEN}`;
  } else if (typeof OpenAPI.TOKEN === 'function') {
    const token = await OpenAPI.TOKEN({
      method: 'GET',
      url,
      headers,
      body: undefined,
      mediaType: undefined,
      path: {},
      cookies: {},
      query: { limit, offset, ...options },
    });
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(url, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    throw await apiErrorFromResponse(response, url, 'Failed to fetch contracts');
  }

  const data: ContractListResponse = await response.json();

  // Transform the response data to frontend format
  const transformedData = ContractTransformer.arrayToFrontend<TransformedContract>(data.data);

  return {
    data: transformedData,
    total: data.total_count,
    limit: data.limit,
    offset: data.offset,
  };
}

/**
 * Hook to fetch paginated contracts with server-side pagination, filtering, and sorting.
 *
 * @param initialPageSize - Initial page size (default: 50)
 * @param options - Filter and sort options
 * @returns Paginated query result with filter/sort-aware refetching
 */
export function useContractsPaginated(
  initialPageSize = 50,
  options?: ContractsPaginatedOptions
) {
  // Include options in the query key so changes trigger refetch
  const queryKeyWithOptions = (params: { limit: number; offset: number }) => [
    ...contractKeys.listPaginated(params),
    {
      status: options?.status,
      labelIds: options?.labelIds,
      childId: options?.childId,
      search: options?.search,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
      subsidised: options?.subsidised,
      subsidyDecisionPending: options?.subsidyDecisionPending,
      hasStaffNotes: options?.hasStaffNotes,
      startDateFrom: options?.startDateFrom,
      startDateTo: options?.startDateTo,
      endDateFrom: options?.endDateFrom,
      endDateTo: options?.endDateTo,
      createdFrom: options?.createdFrom,
      createdTo: options?.createdTo,
    }
  ];

  return usePaginatedQuery<TransformedContract>({
    queryKey: queryKeyWithOptions,
    queryFn: (limit, offset) => fetchContractsPaginated(limit, offset, options),
    initialPageSize,
  });
}

/**
 * One page of the contracts list with the page held by the caller, so the
 * contracts page can keep page, sort, search and filters in the URL and Back
 * from a contract lands on the same page.
 */
export function useContractsPage(options: ContractsPaginatedOptions, pagination: PaginationState) {
  const { isSignedIn, orgId } = useAuth();
  const { isAuthReady } = useAuthContext();
  const canReadContracts = useModuleGate(CONTRACT_READ_MODULES);
  const limit = pagination.pageSize;
  const offset = pagination.pageIndex * pagination.pageSize;

  const query = useQuery({
    queryKey: [...contractKeys.lists(), 'page', orgId ?? null, { limit, offset, ...options }] as const,
    queryFn: () => fetchContractsPaginated(limit, offset, options),
    enabled: !!isSignedIn && isAuthReady && canReadContracts,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    retry: retryUnlessClientError,
  });

  return {
    data: query.data?.data ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

// The list endpoint clamps `limit` to 100; a larger page would come back short
// and end the loop after the first page.
const EXPORT_PAGE_SIZE = 100;
// 100 x 100 = 10,000 contracts: far beyond any organisation; stops a wrong total from looping.
const EXPORT_MAX_PAGES = 100;

/** Every contract matching the list's current search and filters, for "Export all matching". */
export async function fetchAllContracts(
  options: ContractsPaginatedOptions,
  maxPages = EXPORT_MAX_PAGES,
): Promise<TransformedContract[]> {
  const rows: TransformedContract[] = [];
  for (let page = 0; page < maxPages; page++) {
    const result = await fetchContractsPaginated(EXPORT_PAGE_SIZE, page * EXPORT_PAGE_SIZE, options);
    rows.push(...result.data);
    if (result.data.length < EXPORT_PAGE_SIZE || rows.length >= result.total) break;
  }
  return rows;
}

/**
 * Every contract matching `options`, paged through up to `maxPages` pages of
 * 100. For short lists that must not stop at one page, such as the contracts
 * the month-end run may hold back.
 */
export function useAllContracts(options: ContractsPaginatedOptions, maxPages: number) {
  const { isSignedIn, orgId } = useAuth();
  const { isAuthReady } = useAuthContext();
  const canReadContracts = useModuleGate(CONTRACT_READ_MODULES);

  const query = useQuery({
    queryKey: [...contractKeys.lists(), 'all', orgId ?? null, { maxPages, ...options }] as const,
    queryFn: () => fetchAllContracts(options, maxPages),
    enabled: !!isSignedIn && isAuthReady && canReadContracts,
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessClientError,
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

// Hook to fetch all contracts
export function useContracts(
  status?: string,
  childId?: string,
  limit = 50,
  offset = 0
) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  const canReadContracts = useModuleGate(CONTRACT_READ_MODULES);

  return useQuery({
    queryKey: contractKeys.list({ status, childId, limit, offset }),
    queryFn: async (): Promise<TransformedContractListResponse> => {
      const response = await ContractsService.getApiV1Contracts(
        status,
        childId,
        undefined,    // search
        'created_at', // sortBy
        'desc',       // sortOrder
        limit,
        offset
      );
      // The API returns the contract list under `data` in backend casing. Build a
      // new object rather than writing a `contracts` property onto the generated
      // response — mutating it made the declared type lie and poisoned inference
      // in every consumer (#948).
      return {
        contracts: ContractTransformer.arrayToFrontend<TransformedContract>(response?.data ?? []),
        total_count: response?.total_count,
        limit: response?.limit,
        offset: response?.offset,
      };
    },
    enabled: isSignedIn && isAuthReady && canReadContracts,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: retryUnlessClientError,
  });
}

// Hook to fetch a single contract
export function useContract(id: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: contractKeys.detail(id),
    queryFn: async () => {
      const response = await ContractsService.getApiV1Contracts1(id);
      // Transform single contract to frontend format
      return ContractTransformer.toFrontend<TransformedContract>(response);
    },
    enabled: !!id && isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook to create a new contract
export function useCreateContract() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async (contract: CreateContractRequest) => {
      // Transform to backend format (keys only). Do not mutate date fields here.
      // Date handling is already normalized by callers (either string or { time })
      const backendContract =
        ContractTransformer.toBackend<billing_CreateContractRequest>(contract);
      const response = await ContractsService.postApiV1Contracts(backendContract);
      // Transform response to frontend format
      return ContractTransformer.toFrontend<TransformedContract>(response);
    },
    onSuccess: async () => {
      // Invalidate capacity children queries since contracts affect child categorization
      queryClient.invalidateQueries({ queryKey: capacityKeys.children() });
      // A contract change decides what the next invoice bills and what the
      // debtor owes, so the billing surfaces must move with it — including the
      // ones open in the operator's other browser tabs, which a local
      // invalidateQueries can never reach.
      await invalidateMoneyViews(queryClient);
      toast.success(i18n._('common.success.created'));
    },
    onError: (error: unknown) => {
      toast.error(errorToastText(error, i18n, msg({ id: 'common.error.createFailed', message: 'Failed to create' })));
    },
  });
}

// Hook to update a contract
export function useUpdateContract(options?: {
  /** No success toast: the caller saves several parts of the contract and reports once. */
  silent?: boolean;
}) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  const silent = options?.silent ?? false;

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateContractRequest }) => {
      // Transform to backend format (keys only). Do not mutate date fields here.
      const backendData =
        ContractTransformer.toBackend<billing_UpdateContractRequest>(data);
      const response = await ContractsService.putApiV1Contracts(id, backendData);
      // Transform response to frontend format
      return ContractTransformer.toFrontend<TransformedContract>(response);
    },
    onSuccess: async (_data, variables) => {
      // Invalidate capacity children queries since contract updates affect child categorization
      queryClient.invalidateQueries({ queryKey: capacityKeys.children() });
      // Invalidate entity-labels cache for this contract
      queryClient.invalidateQueries({ queryKey: ['entity-labels', 'contracts', variables.id] });
      // A contract change decides what the next invoice bills and what the
      // debtor owes, so the billing surfaces must move with it — including the
      // ones open in the operator's other browser tabs, which a local
      // invalidateQueries can never reach.
      //
      // contractKeys.all is in that pass, and it is a prefix of lists(),
      // detail() and amends(). The pending-amend banner therefore re-evaluates
      // on the spot — it must, both to appear after a subsidy edit and to
      // disappear once that edit is reverted — without a second, later drop
      // that would refetch the same preview twice.
      await invalidateMoneyViews(queryClient);
      if (!silent) {
        toast.success(i18n._('common.success.updated'));
      }
    },
    onError: (error: unknown) => {
      // Invoice-collision 422s are surfaced by the BlockingInvoicesDialog in the
      // component; suppress the toast here (the rejection still propagates).
      if (isInvoiceCollisionError(error)) {
        return;
      }
      console.error('Failed to update contract:', error);
      const { code } = extractBackendError(error);
      toast.error(translateBackendError(code, i18n, msg`Failed to update contract`));
    },
  });
}

// Hook to terminate a contract
// Hook to delete a contract
export function useDeleteContract() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      // The delete endpoint answers with no body (the generated signature is
      // `CancelablePromise<void>`), so there is nothing to transform — the
      // previous truthiness test on a `void` value could never be true.
      await ContractsService.deleteApiV1Contracts(id);
    },
    onSuccess: async (_, variables) => {
      queryClient.removeQueries({ queryKey: contractKeys.detail(variables.id) });
      // Invalidate capacity children queries since deletion affects child categorization
      queryClient.invalidateQueries({ queryKey: capacityKeys.children() });
      // A contract change decides what the next invoice bills and what the
      // debtor owes, so the billing surfaces must move with it — including the
      // ones open in the operator's other browser tabs, which a local
      // invalidateQueries can never reach.
      await invalidateMoneyViews(queryClient);
      toast.success(i18n._('common.success.deleted'));
    },
    onError: (error: unknown) => {
      console.error('Failed to delete contract:', error);
      const { code } = extractBackendError(error);
      toast.error(translateBackendError(code, i18n, msg`Failed to delete contract`));
    },
  });
}

// Hook to update contract payers
export function useUpdateContractPayers(options?: {
  /** No success toast: the caller saves several parts of the contract and reports once. */
  silent?: boolean;
}) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  const silent = options?.silent ?? false;

  return useMutation({
    mutationFn: async ({ id, payers }: { id: string; payers: PayerRequest[] }) => {
      // Transform payers to backend format
      const backendPayers = payers.map((payer) =>
        ContractTransformer.toBackend<billing_PayerRequest>(payer)
      );
      const response = await ContractsService.putApiV1ContractsPayers(id, backendPayers);
      // Transform response to frontend format
      return response ? ContractTransformer.toFrontend(response) : response;
    },
    onSuccess: async () => {
      // A contract change decides what the next invoice bills and what the
      // debtor owes, so the billing surfaces must move with it — including the
      // ones open in the operator's other browser tabs, which a local
      // invalidateQueries can never reach.
      await invalidateMoneyViews(queryClient);
      if (!silent) {
        toast.success(i18n._('contracts.toast.updatePayersSuccess'));
      }
    },
    onError: (error: unknown) => {
      console.error('Failed to update payers:', error);
      toast.error(errorToastText(error, i18n, msg`Failed to update payers`));
    },
  });
}

// Hook to update a single field of a contract (for table editing)
export function useUpdateContractField() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async ({ 
      id, 
      field, 
      value 
    }: { 
      id: string; 
      // Camel-cased (`TransformedContract`), not the snake_case wire response:
      // the caller edits a row the transformer has already produced, and the
      // date-field check below names `startDate`/`endDate`. Typed on `Contract`
      // (the snake_case DTO) that check compared disjoint types and could never
      // fire, so a Date value was posted unformatted.
      field: keyof TransformedContract;
      value: TransformedContract[keyof TransformedContract] 
    }) => {
      // Format date fields to match the contract form behavior
      let processedValue = value;
      if ((field === 'startDate' || field === 'endDate') && value instanceof Date) {
        processedValue = format(value, "yyyy-MM-dd'T'00:00:00'Z'");
      }
      
      // Create update object with only the changed field
      const updateData = { [field]: processedValue } as UpdateContractRequest;
      // Transform to backend format
      const backendData =
        ContractTransformer.toBackend<billing_UpdateContractRequest>(updateData);
      const response = await ContractsService.putApiV1Contracts(id, backendData);
      // Transform response to frontend format
      return ContractTransformer.toFrontend<TransformedContract>(response);
    },
    onSuccess: (data, variables) => {
      // Update the specific contract in the cache
      queryClient.setQueryData(contractKeys.detail(variables.id), data);
      
      // Update the contract in any list queries
      type ContractListData = {
        contracts?: Contract[];
        limit?: number;
        offset?: number;
        total?: number;
      };
      
      queryClient.setQueriesData<ContractListData>(
        { queryKey: ['contracts', 'list'] },
        (oldData) => {
          if (!oldData?.contracts) return oldData;
          return {
            ...oldData,
            contracts: oldData.contracts.map((contract) =>
              contract.id === variables.id ? { ...contract, [variables.field]: variables.value } : contract
            ),
          };
        }
      );
      
      // Show success message
      toast.success(i18n._('common.success.updated'));
    },
    onError: (error: Error) => {
      throw error; // Re-throw to be handled by the table component
    },
  });
}

// Lightweight active-contract counts for the analytics Children KPIs (P3).
// The full /contracts/active endpoint resolves a child record per contract
// (an N+1) purely to attach display names — the KPIs only need counts. This
// hook hits the dedicated /contracts/active/summary endpoint via the generated
// client (auth handled by OpenAPI.TOKEN), which returns COUNT(*) contracts +
// COUNT(DISTINCT child_id) children with no per-child fan-out. Counts are
// optional in the generated shape; consumers default them (`?? 0`).
export type ActiveContractsSummary = billing_ActiveContractsSummaryResponse;

export function useActiveContractsSummary(billingDate?: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery<ActiveContractsSummary>({
    queryKey: [...contractKeys.all, 'active', 'summary', billingDate] as const,
    queryFn: () => ContractsService.getApiV1ContractsActiveSummary(billingDate),
    enabled: isSignedIn && isAuthReady && !!billingDate,
    staleTime: 2 * 60 * 1000,
  });
}

// Helper hook to get formatted contract data for display
export function useContractWithDetails(contractId: string) {
  const contractQuery = useContract(contractId);

  return {
    contract: contractQuery.data,
    isLoading: contractQuery.isLoading,
    error: contractQuery.error,
  };
}